import React, { useCallback, useEffect, useState } from "react";

import { useAppRouteParams } from "@/AppRoutes";
import { getAdminFrontendAddon } from "@/api/admin/frontendAddons";
import {
  getAdminFrontendMasterOrgs,
  postAdminFrontendMasterAssign,
  postAdminFrontendMasterUnassign,
} from "@/api/admin/frontendMasters";
import { usePageTitleEffect } from "@/components/AppProvider/AppOrgsAndInvitedOrgsProvider";
import { FrontendAddonFormEdit } from "@/components/FrontendAddonForm/FrontendAddonFormEdit";
import { FrontendAddonEditableNotify } from "@/components/FrontendAddonForm/FrontendAddonFormEdit/components/FrontendAddonEditableNotify";
import { FrontendAddonHeader } from "@/components/FrontendAddonHeader";
import { LoadingCover } from "@/components/LoadingCover";
import { OrgAssignmentDualListBox } from "@/components/OrgAssignmentDualListBox";
import { FrontendAddonWithTargetVersionRaw } from "@/domain/FrontendAddon";
import { OrganizationRaw } from "@/domain/Organization";
import { useOrgAssignment } from "@/hooks/useOrgAssignment";
import { AppTopNavSlot } from "../../components/AppTopNav";
import { ErrorWrapper } from "../../components/ErrorWrapper";
import parentStyles from "../index.module.css";

export const PageAdminFrontendMastersEdit: React.FC = () => {
  const { organizationId, frontendAddonId } = useAppRouteParams();
  const [, throwReactError] = useState<never>();
  usePageTitleEffect("フロントエンドアドオンマスタ編集");

  const [targetFrontendAddon, setTargetFrontendAddon] = useState<
    FrontendAddonWithTargetVersionRaw | undefined
  >(undefined);
  const fetchFrontendAddon = useCallback(async () => {
    try {
      const addon = await getAdminFrontendAddon({ frontendAddonId });
      setTargetFrontendAddon(addon);
    } catch (e) {
      throwReactError(() => {
        throw e;
      });
    }
  }, [frontendAddonId]);
  useEffect(() => {
    fetchFrontendAddon();
  }, [fetchFrontendAddon]);

  const [
    { currentAssignedOrgs, isCurrentAssignedOrgsLoaded },
    setCurrentAssignedOrgs,
  ] = useState<{
    currentAssignedOrgs: OrganizationRaw[];
    isCurrentAssignedOrgsLoaded: boolean;
  }>({
    currentAssignedOrgs: [],
    isCurrentAssignedOrgsLoaded: false,
  });

  // アドオン再取得で参照だけ変わったときに保存前の組織割り当ての差分を消さないよう、対象が変わったときだけ取り直す
  useEffect(() => {
    if (!targetFrontendAddon) return;
    const fetchAssignedOrgs = async () => {
      const orgs = await getAdminFrontendMasterOrgs({
        frontendAddonId: targetFrontendAddon.id,
      });
      setCurrentAssignedOrgs({
        currentAssignedOrgs: orgs,
        isCurrentAssignedOrgsLoaded: true,
      });
    };
    if (isCurrentAssignedOrgsLoaded) {
      setCurrentAssignedOrgs({
        currentAssignedOrgs: [],
        isCurrentAssignedOrgsLoaded: false,
      });
    }
    fetchAssignedOrgs();
  }, [targetFrontendAddon?.id]);

  const assignment = useOrgAssignment({
    currentAssignedOrgs,
    isCurrentAssignedOrgsLoaded,
  });

  const renderExtraFields = ({
    isDisableForm,
  }: {
    isDisableForm: boolean;
  }): {
    node: React.ReactNode;
    isDisabledSave?: boolean;
  } => {
    return {
      node: (
        <OrgAssignmentDualListBox
          assignment={assignment}
          disabled={isDisableForm}
        />
      ),
    };
  };

  const onSave = async () => {
    if (!targetFrontendAddon) return;
    const { left, right } = assignment.diffAssignedState;
    await Promise.all([
      ...left.map((org) =>
        postAdminFrontendMasterUnassign({
          organizationId: org.id,
          frontendAddonId: targetFrontendAddon.id,
        })
      ),
      ...right.map((org) =>
        postAdminFrontendMasterAssign({
          organizationId: org.id,
          frontendAddonId: targetFrontendAddon.id,
          targetVersion: { target_version: "latest" },
        })
      ),
    ]);
  };

  return (
    <div className={parentStyles.root}>
      <div>
        <AppTopNavSlot>
          <FrontendAddonHeader
            parentTitle="フロントエンドアドオンマスタ"
            addonName={targetFrontendAddon?.name}
            listPath="/admin/frontendmasters"
          />
        </AppTopNavSlot>
      </div>
      <FrontendAddonEditableNotify targetFrontendAddon={targetFrontendAddon} />
      <div className={parentStyles.pageContent}>
        <ErrorWrapper>
          {targetFrontendAddon ? (
            <FrontendAddonFormEdit
              returnPath="/admin/frontendmasters"
              versionUpdatePath={`/admin/frontendmasters/${frontendAddonId}/frontend_addon_version_update`}
              showVersionSettings={false}
              {...{
                targetFrontendAddon,
                runReloadFrontendAddons: fetchFrontendAddon,
                renderExtraFields,
                onSave,
              }}
            />
          ) : (
            <LoadingCover />
          )}
        </ErrorWrapper>
      </div>
    </div>
  );
};
